var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { LoadingButton } from '@mui/lab';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Link, Radio, RadioGroup, Stack, TextField, Typography, } from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { OrgShrinkageFactorType, ShrinkageCategory } from '../../../enum';
import { useTerminology, useTerminologyTranslation } from '../../app/terminology';
import { Autocomplete, ErrorMessageBox, UnControlledFormField } from '../../common';
import { MAX_TEXT_LENGTH } from '../constants';
import { WizardContext } from '../context';
import { useQueueAPI, useQueuesAPI } from '../queue/selectors';
import { useQueueLocations } from '../selectors';
import { useDeleteShrinkageFactorAPI, useUpdateShrinkageFactorAPI } from './selectors';
var DeleteOptionLabel = function (_a) {
    var deleteScope = _a.deleteScope;
    var context = useTerminology();
    var queueLocations = useQueueLocations();
    var hasLocations = !!queueLocations.length;
    var transKey = (function () {
        if (deleteScope === 'queue') {
            return hasLocations ? 'deleteScopeQueueLocations' : 'deleteScopeQueue';
        }
        return 'deleteScopeAllLocations';
    })();
    return (_jsx(Trans, { i18nKey: "wizard:shrinkage.".concat(transKey), context: context, components: {
            b: _jsx("strong", {}),
        } }));
};
var normalizeSubcategory = function (subcategory) { return (subcategory === null || subcategory === void 0 ? void 0 : subcategory.trim()) || undefined; };
export var EditShrinkageFactorDialog = function (_a) {
    var _b, _c, _d;
    var open = _a.open, factor = _a.factor, onClose = _a.onClose;
    var t = useTerminologyTranslation(['translation', 'wizard']).t;
    var _e = useUpdateShrinkageFactorAPI(), updateData = _e[0], updateFactor = _e[1], clearUpdateFactor = _e[2];
    var _f = useDeleteShrinkageFactorAPI(), deleteData = _f[0], deleteFactor = _f[1], clearDeleteFactor = _f[2];
    var _g = useQueueAPI(), fetchQueue = _g[1];
    var _h = useQueuesAPI(), fetchQueues = _h[1];
    var _j = useState(''), name = _j[0], setName = _j[1];
    var _k = useState(''), subcategory = _k[0], setSubcategory = _k[1];
    var _l = useState(ShrinkageCategory.OutOfOffice), category = _l[0], setCategory = _l[1];
    var _m = useState(true), planned = _m[0], setPlanned = _m[1];
    var _o = useState(false), unplanned = _o[0], setUnplanned = _o[1];
    var _p = useState(false), deleteConfirmOpen = _p[0], setDeleteConfirmOpen = _p[1];
    var _q = useState('queue'), deleteScope = _q[0], setDeleteScope = _q[1];
    var _r = useState(false), applyAcrossQueues = _r[0], setApplyAcrossQueues = _r[1];
    var handleSaveWithoutValidation = useContext(WizardContext).handleSaveWithoutValidation;
    var categoryOptions = useMemo(function () { return [
        { id: ShrinkageCategory.OutOfOffice, name: t('wizard:shrinkage.category.out_of_office') },
        { id: ShrinkageCategory.InOffice, name: t('wizard:shrinkage.category.in_office') },
    ]; }, [t]);
    useEffect(function () {
        var _a, _b;
        if (!open || !factor)
            return;
        setName(factor.name);
        setSubcategory((_a = factor.shrinkageSubcategory) !== null && _a !== void 0 ? _a : '');
        setCategory((_b = factor.shrinkageCategory) !== null && _b !== void 0 ? _b : ShrinkageCategory.OutOfOffice);
        setPlanned(factor.shrinkageType !== OrgShrinkageFactorType.Unplanned);
        setUnplanned(factor.shrinkageType === OrgShrinkageFactorType.Unplanned);
        setDeleteConfirmOpen(false);
        setDeleteScope('queue');
        setApplyAcrossQueues(false);
    }, [factor, open]);
    useEffect(function () {
        if (updateData.data || deleteData.data) {
            fetchQueue();
            fetchQueues();
            onClose();
            setDeleteConfirmOpen(false);
            handleSaveWithoutValidation === null || handleSaveWithoutValidation === void 0 ? void 0 : handleSaveWithoutValidation('submit');
        }
        return function () {
            clearUpdateFactor();
            clearDeleteFactor();
        };
    }, [
        clearDeleteFactor,
        clearUpdateFactor,
        deleteData.data,
        fetchQueue,
        fetchQueues,
        handleSaveWithoutValidation,
        onClose,
        updateData.data,
    ]);
    var isDialogDirty = useMemo(function () {
        var _a, _b, _c;
        if (!factor)
            return false;
        var initialPlanned = factor.shrinkageType !== OrgShrinkageFactorType.Unplanned;
        var initialUnplanned = factor.shrinkageType === OrgShrinkageFactorType.Unplanned;
        if (factor.isParentFactor) {
            return (name !== factor.name ||
                category !== ((_a = factor.shrinkageCategory) !== null && _a !== void 0 ? _a : ShrinkageCategory.OutOfOffice) ||
                applyAcrossQueues !== false);
        }
        return (name !== factor.name ||
            subcategory !== ((_b = factor.shrinkageSubcategory) !== null && _b !== void 0 ? _b : '') ||
            category !== ((_c = factor.shrinkageCategory) !== null && _c !== void 0 ? _c : ShrinkageCategory.OutOfOffice) ||
            planned !== initialPlanned ||
            unplanned !== initialUnplanned ||
            applyAcrossQueues !== false);
    }, [factor, name, subcategory, category, planned, unplanned, applyAcrossQueues]);
    if (!factor)
        return null;
    var trimmedName = name.trim();
    var shrinkageTypes = [
        planned ? OrgShrinkageFactorType.Planned : undefined,
        unplanned ? OrgShrinkageFactorType.Unplanned : undefined,
    ].filter(Boolean);
    var canUpdate = factor.isParentFactor
        ? Boolean(trimmedName) && isDialogDirty
        : Boolean(trimmedName) && shrinkageTypes.length > 0 && isDialogDirty;
    var basePayload = {
        id: factor.id,
        queueId: factor.queueId,
        queueLocationId: (_b = factor.queueLocationId) !== null && _b !== void 0 ? _b : undefined,
        name: factor.name,
        shrinkageCategory: (_c = factor.shrinkageCategory) !== null && _c !== void 0 ? _c : undefined,
        shrinkageSubcategory: normalizeSubcategory(factor.shrinkageSubcategory),
        shrinkageType: (_d = factor.shrinkageType) !== null && _d !== void 0 ? _d : undefined,
        isParentFactor: factor.isParentFactor,
    };
    var handleUpdate = function () {
        if (!canUpdate)
            return;
        updateFactor(__assign(__assign({}, basePayload), { updatedName: trimmedName, updatedShrinkageCategory: category, updatedShrinkageSubcategory: factor.isParentFactor ? undefined : normalizeSubcategory(subcategory), updatedShrinkageTypes: factor.isParentFactor ? [] : shrinkageTypes, applyAcrossQueues: applyAcrossQueues }));
    };
    var handleDelete = function () {
        deleteFactor(__assign(__assign({}, basePayload), { allQueues: deleteScope === 'all' }));
    };
    var requesting = !!updateData.requesting || !!deleteData.requesting;
    return (_jsxs(_Fragment, { children: [_jsxs(Dialog, __assign({ open: open && !deleteConfirmOpen, onClose: onClose, maxWidth: "sm", fullWidth: true }, { children: [_jsx(DialogTitle, __assign({ component: "div" }, { children: _jsx(Typography, __assign({ variant: "h3" }, { children: t('wizard:shrinkage.editFactorTitle') })) })), _jsx(DialogContent, { children: _jsxs(Stack, __assign({ spacing: 5 }, { children: [_jsx(ErrorMessageBox, { errors: [updateData.error, deleteData.error], scrollTop: false }), _jsx(UnControlledFormField, __assign({ required: true, label: t('wizard:shrinkage.shrinkageFactor') }, { children: _jsx(TextField, { value: name, onChange: function (e) { return setName(e.target.value); }, inputProps: { maxLength: MAX_TEXT_LENGTH } }) })), !factor.isParentFactor && (_jsx(UnControlledFormField, __assign({ label: t('wizard:shrinkage.subcategory') }, { children: _jsx(TextField, { value: subcategory, onChange: function (e) { return setSubcategory(e.target.value); }, inputProps: { maxLength: MAX_TEXT_LENGTH } }) }))), _jsxs(Stack, __assign({ direction: "row", spacing: 3, alignItems: "flex-end" }, { children: [_jsx(UnControlledFormField, __assign({ required: true, label: t('wizard:shrinkage.type'), sx: { flex: 1 } }, { children: _jsx(Autocomplete, { options: categoryOptions, value: category, getOptionValue: function (option) { return option.id; }, onChange: function (value) { var _a; return setCategory((_a = value) !== null && _a !== void 0 ? _a : ShrinkageCategory.OutOfOffice); } }) })), !factor.isParentFactor && (_jsxs(Stack, __assign({ direction: "row", spacing: 1 }, { children: [_jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: planned, onChange: function (e) { return setPlanned(e.target.checked); }, size: "small" }), label: t('wizard:shrinkage.planned') }), _jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: unplanned, onChange: function (e) { return setUnplanned(e.target.checked); }, size: "small" }), label: t('wizard:shrinkage.unplanned') })] })))] })), _jsx(Box, __assign({ sx: { py: 0 } }, { children: _jsx(FormControlLabel, { control: _jsx(Checkbox, { size: "small", checked: applyAcrossQueues, onChange: function (e) { return setApplyAcrossQueues(e.target.checked); } }), label: t('wizard:shrinkage.applyChangeAcrossQueues') }) }))] })) }), _jsxs(DialogActions, { children: [_jsx(Link, __assign({ component: "button", onClick: function () { return setDeleteConfirmOpen(true); }, disabled: requesting }, { children: t('wizard:shrinkage.deleteFactor') })), _jsxs(Stack, __assign({ direction: "row", spacing: 2 }, { children: [_jsx(Button, __assign({ variant: "outlined", onClick: onClose }, { children: t('translation:action.cancel') })), _jsx(LoadingButton, __assign({ variant: "contained", onClick: handleUpdate, disabled: !canUpdate, loading: requesting }, { children: t('translation:action.update') }))] }))] })] })), _jsxs(Dialog, __assign({ open: open && deleteConfirmOpen, onClose: function () { return setDeleteConfirmOpen(false); }, maxWidth: "sm", fullWidth: true }, { children: [_jsx(DialogTitle, __assign({ component: "div" }, { children: _jsx(Typography, __assign({ variant: "h3" }, { children: t('wizard:shrinkage.deleteFactorConfirmTitle', { name: factor.name }) })) })), _jsx(DialogContent, { children: _jsxs(Stack, __assign({ spacing: 3 }, { children: [_jsx(ErrorMessageBox, { errors: [deleteData.error], scrollTop: false }), _jsxs(RadioGroup, __assign({ value: deleteScope, onChange: function (e) { return setDeleteScope(e.target.value); } }, { children: [_jsx(FormControlLabel, { value: "queue", control: _jsx(Radio, {}), label: _jsx(DeleteOptionLabel, { deleteScope: "queue" }) }), _jsx(FormControlLabel, { value: "all", control: _jsx(Radio, {}), label: _jsx(DeleteOptionLabel, { deleteScope: "all" }) })] }))] })) }), _jsxs(DialogActions, __assign({ sx: { justifyContent: 'space-between' } }, { children: [_jsx(Button, __assign({ variant: "outlined", onClick: function () { return setDeleteConfirmOpen(false); } }, { children: t('translation:action.cancel') })), _jsx(LoadingButton, __assign({ variant: "contained", onClick: handleDelete, loading: requesting }, { children: t('wizard:shrinkage.deleteButton') }))] }))] }))] }));
};
